import { useEffect, useState, type ReactNode } from "react"
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion"
import { Cross as Hamburger } from "hamburger-react"
import InstagramIcon from "../components/icons/InstagramIcon"
import WhatsAppIcon from "../components/icons/WhatsAppIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { useJitsu } from "@jitsu/jitsu-react"
import { getOrCreateSessionId } from "../lib/session"

const FILTER_HEIGHT = 48

type Props = {
  logoSrc?: string
  logoAlt?: string
  filterSlot?: ReactNode
}

const MENU_ITEMS = [
  { label: "Home", type: "scroll", targetId: "home" },
  { label: "Iniciar tratamento", type: "external", href: "https://app.nativamed.com.br" },
  { label: "Blog", type: "internal", path: "/blogs" },
  { label: "Depoimentos", type: "scroll", targetId: "videos" },
  { label: "Galeria", type: "scroll", targetId: "galeria" },
  { label: "Dúvidas", type: "scroll", targetId: "faq" },
]

export default function Header({
  logoSrc = "/logo.webp",
  logoAlt = "Nativamed",
  filterSlot,
}: Props) {
  const { scrollY } = useScroll()
  const [scrolled, setScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [headerHeights, setHeaderHeights] = useState({ expanded: 74, collapsed: 50 })
  const [menuSize, setMenuSize] = useState(24)

  const navigate = useNavigate()
  const location = useLocation()

  const isBlogPage = location.pathname.startsWith("/blogs")
  const filterHeight = filterSlot ? FILTER_HEIGHT : 0

  const { analytics } = useJitsu()

  function trackClick(label: string) {
    analytics.track("external_link_click", {
      session_id: getOrCreateSessionId(),
      label,
    })
  }

  useMotionValueEvent(scrollY, "change", (latest) => {
    setScrolled(latest > 16)
  })

  useEffect(() => {
    setScrolled(window.scrollY > 16)

    const updateHeaderHeights = () => {
      const w = window.innerWidth

      setIsMobile(w < 1024)

      if (w < 480) {
        setHeaderHeights({ expanded: 40, collapsed: 34 })
        setMenuSize(20)
      } else if (w < 640) {
        setHeaderHeights({ expanded: 64, collapsed: 46 })
        setMenuSize(32)
      } else if (w < 768) {
        setHeaderHeights({ expanded: 68, collapsed: 48 })
        setMenuSize(32)
      } else if (w < 1024) {
        setHeaderHeights({ expanded: 72, collapsed: 50 })
        setMenuSize(32)
      } else {
        setHeaderHeights({ expanded: 74, collapsed: 50 })
        setMenuSize(32)
      }
    }

    updateHeaderHeights()
    window.addEventListener("resize", updateHeaderHeights)
    return () => window.removeEventListener("resize", updateHeaderHeights)
  }, [])

  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = originalOverflow || ""
    }

    return () => {
      document.body.style.overflow = originalOverflow || ""
    }
  }, [isOpen])

  const scrollToSection = (targetId: string) => {
    const el = document.getElementById(targetId)
    if (!el) return

    const y = el.getBoundingClientRect().top + window.scrollY - headerHeights.collapsed - 12

    window.scrollTo({
      top: y,
      behavior: "smooth",
    })

    setOpen(false)
  }

  const handleMenuClick = (item: any) => {
    trackClick(item.label)

    if (item.type === "scroll") {
      if (location.pathname === "/") {
        scrollToSection(item.targetId)
      } else {
        navigate("/", { state: { scrollToId: item.targetId } })
        setOpen(false)
      }
      return
    }

    if (item.type === "internal") {
      navigate(item.path)
      setOpen(false)
      return
    }

    if (item.type === "external") {
      window.open(item.href, "_blank")
      setOpen(false)
    }
  }

  const logoRowHeight = scrolled ? headerHeights.collapsed : headerHeights.expanded

  return (
    <>
      <motion.header
        className={`
          fixed inset-x-0 top-0 z-50
          transition-all duration-300
          ${
            scrolled
              ? "border-b bg-white/10 shadow-[0_0_10px_rgba(0,0,2,0.2)] backdrop-blur-[3px] z-70"
              : isMobile
                ? "bg-transparent backdrop-blur-none"
                : "bg-transparent backdrop-blur-none"
          }
        `}
        animate={{
          height: logoRowHeight + filterHeight,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
      >
        {/* ── Logo row ── */}
        <div
          className="flex items-center justify-between px-4 sm:mx-auto sm:max-w-[90%]"
          style={{ height: logoRowHeight }}
        >
          <a
            href="/"
            className="flex items-center gap-2"
            onClick={(e) => {
              e.preventDefault()

              if (location.pathname === "/") {
                scrollToSection("home")
              } else {
                navigate("/", { state: { scrollToId: "home" } })
                setOpen(false)
              }
            }}
          >
            <img
              src={logoSrc}
              alt={logoAlt}
              className="h-4 w-auto sm:h-6"
              loading="lazy"
              decoding="async"
            />
          </a>

          {/* "| Blog" label on blog pages */}
          {isBlogPage && (
            <div className="flex items-center gap-2 ml-2.5 select-none pointer-events-none">
              <span className="text-gray-300 text-sm font-light">|</span>
              <span className="font-sans text-lg font-light text-primary-darker/70">Blog</span>
            </div>
          )}

          <div className="ml-auto">
            <Hamburger
              size={menuSize}
              label="Menu"
              color="#BABABA"
              toggled={isOpen}
              toggle={setOpen}
            />
          </div>
        </div>

        {/* ── Filter slot (blog listing only) ── */}
        {filterSlot && (
          <div
            className="border-t border-gray-100/60 px-4 sm:mx-auto sm:max-w-[90%] flex items-center gap-2"
            style={{ height: FILTER_HEIGHT }}
          >
            {filterSlot}
          </div>
        )}
      </motion.header>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.button
              type="button"
              aria-label="Fechar menu"
              className="fixed inset-0 z-[61] bg-black/35"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <motion.aside
              className="fixed right-0 top-0 z-[70] flex h-screen w-[86vw] max-w-[420px] flex-col bg-[#f8faf7] px-6 pb-8 pt-24 shadow-[-10px_0_30px_rgba(0,0,0,0.12)] sm:px-8"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
            >
              <nav className="flex flex-col gap-5">
                {MENU_ITEMS.map((item, index) => (
                  <motion.button
                    key={item.targetId}
                    type="button"
                    onClick={() => handleMenuClick(item)}
                    className="group cursor-pointer w-fit text-left font-sans text-[1.35rem] leading-none text-primary-darker sm:text-[1.55rem]"
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 18 }}
                    transition={{ delay: 0.04 * index }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="relative inline-block pb-2">
                      {item.label}
                      <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-[#7dbb6d] transition-transform duration-300 ease-out group-hover:scale-x-100" />
                    </span>
                  </motion.button>
                ))}
              </nav>

              <div className="mt-auto flex items-center gap-4 pt-10">
                <a
                  href="https://www.instagram.com/nativamed/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  onClick={() => trackClick("instagram")}
                  className="rounded-full border border-[#d8e5d5] p-3 text-[#1f3b2f] transition-transform duration-200 hover:scale-105"
                >
                  <InstagramIcon className="h-5 w-5" strokeWidth={2} />
                </a>

                <a
                  href="https://api.whatsapp.com/send/?phone=5548996064932&text=Ol%C3%A1%2C+vim+do+site+e+gostaria+de+mais+informa%C3%A7%C3%B5es&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                  onClick={() => trackClick("whatsapp")}
                  className="rounded-full border border-[#d8e5d5] p-3 text-[#1f3b2f] transition-transform duration-200 hover:scale-105"
                >
                  <WhatsAppIcon className="h-5 w-5" strokeWidth={2} />
                </a>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
